import { useEffect, useState } from "react";
import { Section, Selection } from "./types";
import { LiteEvent } from "../../lib/lite-event";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

export const useConfigurator = (sections: Section[]) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [init, setInit] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Selection>({});

  const [callbacks, _] = useState({
    onSelect: new LiteEvent<{ sectionId: string; optionId: string }>(),
    onDeselect: new LiteEvent<{ sectionId: string; optionId: string }>(),
    onIncompatible: new LiteEvent<{
      sectionId: string;
      optionId: string;
      conflictOptionId: string;
    }>(),
    onSelectionChanged: new LiteEvent<{ selectedOptions: Selection }>(),
  });

  useEffect(() => {
    callbacks.onSelectionChanged.trigger({ selectedOptions });
  }, [callbacks.onSelectionChanged, selectedOptions]);

  const getAllSelectedOptionIds = () => {
    return Object.values(selectedOptions).flat();
  };

  const canSelectOption = (sectionId: string, optionId: string) => {
    const section = sections.find((sec) => sec.id === sectionId);
    if (!section) return false;

    const option = section.options.find((opt) => opt.id === optionId);
    if (!option) return false;

    const allSelectedOptions = getAllSelectedOptionIds();

    if (option.dependsOn) {
      const { ids, condition } = option.dependsOn;
      if (condition === "all") {
        if (!ids.every((dep) => allSelectedOptions.includes(dep))) {
          return false;
        }
      } else if (condition === "any") {
        if (!ids.some((dep) => allSelectedOptions.includes(dep))) {
          return false;
        }
      }
    }

    if (option.excludes) {
      for (const excludeId of option.excludes) {
        if (allSelectedOptions.includes(excludeId)) {
          return false;
        }
      }
    }

    return true;
  };

  const getDependents = (optionId: string) => {
    const dependents: string[] = [];
    sections.forEach((section) => {
      section.options.forEach((option) => {
        if (option.dependsOn?.ids.includes(optionId)) {
          dependents.push(option.id);
          dependents.push(...getDependents(option.id));
        }
      });
    });
    return dependents;
  };

  const isStillDependent = (selection: Selection, dependentId: string) => {
    const option = sections
      .flatMap((section) => section.options)
      .find((opt) => opt.id === dependentId);
    if (!option || !option.dependsOn) return false;

    const { ids, condition } = option.dependsOn;
    const allSelectedOptions = Object.values(selection).flat();

    if (condition === "all") {
      return ids.every((dep) => allSelectedOptions.includes(dep));
    } else if (condition === "any") {
      return ids.some((dep) => allSelectedOptions.includes(dep));
    }

    return false;
  };
  const filterDependents = (
    previousSelection: Selection,
    newSelection: Selection
  ) => {
    // Deselect previously selected options
    const previouslySelectedOptions = Object.values(previousSelection).flat();
    for (const prevOptionId of previouslySelectedOptions) {
      const dependents = getDependents(prevOptionId);
      for (const depId of dependents) {
        const depSectionId = sections.find((sec) =>
          sec.options.find((opt) => opt.id === depId)
        )?.id;

        const stillDependant = isStillDependent(newSelection, depId);
        if (depSectionId && !stillDependant) {
          newSelection[depSectionId] = (
            newSelection[depSectionId] || []
          ).filter((id) => id !== depId);
        }
      }
    }

    return newSelection;
  };

  // Handle exclusions: deselect options that are excluded by newly selected options
  const handleExclusions = (newSelection: Selection) => {
    const allSelectedOptions = Object.values(newSelection).flat();

    // Find all options that should be deselected due to exclusions
    sections.forEach((section) => {
      section.options.forEach((option) => {
        if (option.excludes && allSelectedOptions.includes(option.id)) {
          // This option is selected and has exclusions
          option.excludes.forEach((excludedId) => {
            if (allSelectedOptions.includes(excludedId)) {
              // Find the section containing the excluded option
              const excludedSection = sections.find((sec) =>
                sec.options.find((opt) => opt.id === excludedId)
              );
              if (excludedSection) {
                // Remove the excluded option from selection
                newSelection[excludedSection.id] = (
                  newSelection[excludedSection.id] || []
                ).filter((id) => id !== excludedId);
              }
            }
          });
        }
      });
    });

    return newSelection;
  };

  // Find a valid default option for a section
  const findValidDefaultOption = (
    section: Section,
    currentSelection: Selection
  ): string | null => {
    // First try the section's default selection
    if (section.defaultSelection && section.defaultSelection.length > 0) {
      for (const defaultId of section.defaultSelection) {
        if (
          canSelectOptionWithSelection(section.id, defaultId, currentSelection)
        ) {
          return defaultId;
        }
      }
    }

    // If no default works, find the first available option
    for (const option of section.options) {
      if (
        canSelectOptionWithSelection(section.id, option.id, currentSelection)
      ) {
        return option.id;
      }
    }

    return null;
  };

  // Check if an option can be selected given a specific selection state
  const canSelectOptionWithSelection = (
    sectionId: string,
    optionId: string,
    selection: Selection
  ) => {
    const section = sections.find((sec) => sec.id === sectionId);
    if (!section) return false;

    const option = section.options.find((opt) => opt.id === optionId);
    if (!option) return false;

    const allSelectedOptions = Object.values(selection).flat();

    if (option.dependsOn) {
      const { ids, condition } = option.dependsOn;
      if (condition === "all") {
        if (!ids.every((dep) => allSelectedOptions.includes(dep))) {
          return false;
        }
      } else if (condition === "any") {
        if (!ids.some((dep) => allSelectedOptions.includes(dep))) {
          return false;
        }
      }
    }

    if (option.excludes) {
      for (const excludeId of option.excludes) {
        if (allSelectedOptions.includes(excludeId)) {
          return false;
        }
      }
    }

    return true;
  };

  // Handle required sections: ensure they always have a valid selection
  const ensureRequiredSelections = (newSelection: Selection) => {
    sections.forEach((section) => {
      if (section.required) {
        const currentSelections = newSelection[section.id] || [];

        // Check if current selections are still valid
        const validSelections = currentSelections.filter((optionId) =>
          canSelectOptionWithSelection(section.id, optionId, newSelection)
        );

        if (validSelections.length === 0) {
          // No valid selections remain, find a new default
          const defaultOption = findValidDefaultOption(section, newSelection);
          if (defaultOption) {
            newSelection[section.id] = [defaultOption];
          } else {
            // If no valid option found, keep empty but log a warning
            console.warn(
              `No valid option found for required section: ${section.id}`
            );
            newSelection[section.id] = [];
          }
        } else {
          // Update with only the valid selections
          newSelection[section.id] = validSelections;
        }
      }
    });

    return newSelection;
  };

  const toggleOption = (sectionId: string, optionId: string) => {
    if (isOptionSelected(optionId)) {
      deselectOption(sectionId, optionId);
    } else {
      selectOption(sectionId, optionId);
    }
  };

  const selectOption = (sectionId: string, optionId: string) => {
    const section = sections.find((sec) => sec.id === sectionId);
    if (!section || !canSelectOption(sectionId, optionId)) {
      // Find all currently selected options that conflict with this option
      const allSelectedOptionIds = getAllSelectedOptionIds();
      const conflictingOptions = sections
        .flatMap((sect) => sect.options) // Get all options from all sections
        .filter(
          (opt) =>
            opt.excludes?.includes(optionId) && // Option excludes the one we want to select
            allSelectedOptionIds.includes(opt.id) // And the option is currently selected
        );

      // If we found any conflicting options, trigger the incompatible event
      if (conflictingOptions.length > 0 && callbacks.onIncompatible) {
        // We still only pass the first conflict to maintain backward compatibility
        // Our updated SelectionHelperDialog will find all conflicts
        callbacks.onIncompatible.trigger({
          sectionId,
          optionId,
          conflictOptionId: conflictingOptions[0].id,
        });
      }
      return;
    }

    setSelectedOptions((prev) => {
      let newSelectedOptions: Selection = { ...prev };

      // Handle single selection (multiple: false)
      if (!section.multiple) {
        // Select the new option
        newSelectedOptions[sectionId] = [optionId];
      } else {
        // Handle multiple selection (multiple: true)
        newSelectedOptions[sectionId] = [
          ...(newSelectedOptions[sectionId] || []),
          optionId,
        ];
      }

      // Step 1: Handle exclusions - deselect options that conflict with the new selection
      newSelectedOptions = handleExclusions(newSelectedOptions);

      // Step 2: Filter out dependents that no longer have their dependencies met
      newSelectedOptions = filterDependents(prev, newSelectedOptions);

      // Step 3: Ensure required sections still have valid selections
      newSelectedOptions = ensureRequiredSelections(newSelectedOptions);

      return newSelectedOptions;
    });

    callbacks.onSelect.trigger({ sectionId, optionId });
  };

  // Function to deselect an option
  const deselectOption = (sectionId: string, optionId: string) => {
    const section = sections.find((x) => x.id === sectionId) as Section;

    if (!section) return;

    setSelectedOptions((prev) => {
      // For required sections, don't allow deselecting the last option
      if (section.required && prev[sectionId]?.length <= 1) {
        return prev;
      }

      let newSelection: Selection = {
        ...prev,
        [sectionId]: prev[sectionId].filter((id) => id !== optionId),
      };

      // Step 1: Filter out dependents that no longer have their dependencies met
      newSelection = filterDependents(prev, newSelection);

      // Step 2: Ensure required sections still have valid selections
      newSelection = ensureRequiredSelections(newSelection);

      return newSelection;
    });

    callbacks.onDeselect?.trigger({ sectionId, optionId });
  };

  const getAvailableOptions = (sectionId: string) => {
    const section = sections.find((sec) => sec.id === sectionId);
    if (!section) return [];

    return section.options.filter((option) =>
      canSelectOption(sectionId, option.id)
    );
  };

  const getSelectedOptions = (sectionId: string) => {
    const section = sections.find((sec) => sec.id === sectionId);
    if (!section) return [];

    return (selectedOptions[sectionId] || []).map(
      (optionId) => section.options.find((option) => option.id === optionId)!
    );
  };
  const getAllOptions = (sectionId: string) => {
    const section = sections.find((sec) => sec.id === sectionId);
    if (!section) return [];
    return section.options;
  };

  const isOptionSelected = (optionId: string) => {
    return getAllSelectedOptionIds().includes(optionId);
  };

  const getAllSections = () => {
    return sections;
  };

  const setSelectedOptionIds = (ids: string[]) => {
    for (const id of ids) {
      const section = sections.find((sec) =>
        sec.options.find((opt) => opt.id === id)
      );
      if (!section) {
        console.log("cannot find section for: " + id);
        continue;
      }
      selectOption(section?.id, id);
    }
  };

  useEffect(() => {
    if (!init) {
      const itemsParam = searchParams.get("options");
      if (itemsParam) {
        const ids = itemsParam.split(".");

        const selection: Selection = {};

        for (const id of ids) {
          const section = sections.find((sec) =>
            sec.options.find((opt) => opt.id === id)
          );
          if (!section) {
            console.warn("Cannot find section for: " + id);
            continue;
          }

          if (!section.multiple) {
            // Select the new option
            selection[section.id] = [id];
          } else {
            // Handle multiple selection (multiple: true)
            selection[section.id] = [...(selection[section.id] || []), id];
          }
        }
        setSelectedOptions(selection);
      } else {
        // If no options in URL, initialize with defaults
        const defaultSelection: Selection = {};
        for (const section of sections) {
          defaultSelection[section.id] = section.defaultSelection || [];
        }
        setSelectedOptions(defaultSelection);
      }
      setInit(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const ids = Array.from(
      new Set(Object.values(selectedOptions).flat().values())
    );
    const currentOptions = searchParams.get("options");

    // Join the ids to the format used in the URL
    const newOptions = ids.join(".");

    // Check if the current URL already has the same "options" param to avoid redundant updates
    if (newOptions !== currentOptions) {
      const params = new URLSearchParams(searchParams.toString());

      // Update the "options" param while keeping other params intact
      if (newOptions.length > 0) {
        params.set("options", newOptions);
      }
      // Use router.replace to update the URL with the modified search params

      // The nextjs apis causes refetching.
      // https://github.com/vercel/next.js/discussions/48110
      // So we use the history api instead.
      // router.replace(`?${params.toString()}`, { scroll: false });
      window.history.pushState(null, "", `?${params.toString()}`);
    }
  }, [selectedOptions, searchParams, pathname, router]);

  return {
    selectedOptions,

    setSelectedOptions,
    setSelectedOptionIds,
    selectOption,
    deselectOption,
    toggleOption,
    canSelectOption,
    getAvailableOptions,
    getAllSections,
    getAllSelectedOptionIds,
    getSelectedOptions,
    getAllOptions,
    isOptionSelected,
    callbacks,
  };
};
